import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/lib/api'
import type { AdsCatalog, AdsCatalogInput, ServiceConfig, ServiceConfigInput } from '@/lib/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { Tab } from './tabs'
import { blueprintIssues, hasBlueprintIssues } from './lib/blueprint'
import { HIDDEN_TYPES, TypesEditor } from './editors/TypesEditor'
import { SlotsEditor } from './editors/SlotsEditor'
import { SettingsEditor } from './editors/SettingsEditor'
import { BiddersEditor } from './editors/BiddersEditor'

const WP_SERVICE_NAME: Record<string, string> = {
  SW: 'post',
  RB: 'rozrywka',
  BB: 'bizblog',
  AB: 'autoblog',
}

function buildWpExportPreview(cfg: ServiceConfig, catalog: AdsCatalog) {
  const typeMap = new Map(catalog.types.map((t) => [t.id, t]))
  const wpService = WP_SERVICE_NAME[cfg.service] ?? 'post'
  const out: Record<string, unknown> = {}

  const bpMap = new Map(cfg.slotBlueprints.map((bp) => [bp.slot, bp]))
  for (const unit of catalog.adUnits) {
    const t = typeMap.get(unit.typeId)
    const sizes = t ? { ...t.sizes, ...unit.sizes } : { desktop: [], tablet: [], mobile: [] }
    const account = cfg.gpidAccount || cfg.service
    const adUnitPath = `${cfg.gpidNetwork}/${account}//${unit.slot}`
    const transparent =
      unit.slot.includes('XL1') || unit.slot.includes('SCRN') || unit.slot.includes('HEADER')

    out[adUnitPath] = {
      type: 'single',
      adUnit: adUnitPath,
      active: bpMap.get(unit.slot)?.active ?? unit.active,
      deley: false,
      adSizes: JSON.stringify(sizes),
      adHigh: unit.adHigh,
      transparent,
      bgColor: '',
      seperateSlot: '',
      closed: [],
      slotHigh: { desktop: null, tablet: null, mobile: null },
      isEmpty: 1,
      service: wpService,
      addStick: false,
      data_generated_date: '2026-10-21 12:12:06',
    }
  }
  return out
}

export function ServiceEditor({
  service,
  tab,
  onTabChange,
}: {
  service: string
  tab: Tab
  onTabChange: (tab: Tab) => void
}) {
  const queryClient = useQueryClient()

  const catalogQuery = useQuery({
    queryKey: ['ads-catalog'],
    queryFn: api.getAdsCatalog,
  })

  const cfg = useQuery({
    queryKey: ['config', service],
    queryFn: () => api.getConfig(service),
  })

  const [catalogDraft, setCatalogDraft] = useState<AdsCatalog | null>(null)
  const [draft, setDraft] = useState<ServiceConfig | null>(null)
  const [saveStatus, setSaveStatus] = useState<{ kind: 'idle' | 'ok' | 'err'; message?: string }>({
    kind: 'idle',
  })

  useEffect(() => {
    if (catalogQuery.data) {
      setCatalogDraft(catalogQuery.data)
    }
  }, [catalogQuery.data])

  useEffect(() => {
    if (cfg.data) {
      setDraft(cfg.data)
      setSaveStatus({ kind: 'idle' })
    }
  }, [cfg.data])

  const catalogDirty = useMemo(() => {
    if (!catalogQuery.data || !catalogDraft) return false
    return (
      JSON.stringify({ types: catalogQuery.data.types, adUnits: catalogQuery.data.adUnits }) !==
      JSON.stringify({ types: catalogDraft.types, adUnits: catalogDraft.adUnits })
    )
  }, [catalogQuery.data, catalogDraft])

  const configDirty = useMemo(() => {
    if (!cfg.data || !draft) return false
    return JSON.stringify(cfg.data) !== JSON.stringify(draft)
  }, [cfg.data, draft])

  const dirty = catalogDirty || configDirty

  const save = useMutation({
    mutationFn: async () => {
      if (!draft || !cfg.data || !catalogDraft || !catalogQuery.data) {
        throw new Error('config not loaded')
      }

      let savedCatalog = catalogQuery.data
      if (catalogDirty) {
        const catInput: AdsCatalogInput = {
          types: catalogDraft.types,
          adUnits: catalogDraft.adUnits,
          version: catalogQuery.data.version,
        }
        savedCatalog = await api.putAdsCatalog(catInput)
      }

      const { updatedBy: _updatedBy, updatedAt: _updatedAt, ...rest } = draft
      const input: ServiceConfigInput = { ...rest, version: cfg.data.version }
      const savedCfg = await api.putConfig(service, input)
      return { savedCatalog, savedCfg }
    },
    onSuccess: ({ savedCatalog, savedCfg }) => {
      queryClient.setQueryData(['ads-catalog'], savedCatalog)
      queryClient.setQueryData(['config', service], savedCfg)
      queryClient.invalidateQueries({ queryKey: ['config'] })
      setCatalogDraft(savedCatalog)
      setDraft(savedCfg)
      setSaveStatus({
        kind: 'ok',
        message: `Zapisano (katalog v${savedCatalog.version}, ${service} v${savedCfg.version}).`,
      })
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        queryClient.invalidateQueries({ queryKey: ['ads-catalog'] })
        queryClient.invalidateQueries({ queryKey: ['config', service] })
        setSaveStatus({
          kind: 'err',
          message: 'Konflikt wersji — odświeżono dane z serwera, wprowadź zmiany ponownie.',
        })
        return
      }
      if (err instanceof ApiError && err.status === 422) {
        const body = err.body as { issues?: unknown }
        setSaveStatus({
          kind: 'err',
          message: 'Walidacja nieudana: ' + JSON.stringify(body.issues, null, 2),
        })
        return
      }
      setSaveStatus({ kind: 'err', message: (err as Error).message })
    },
  })

  if (cfg.isLoading || catalogQuery.isLoading) {
    return <p className="text-sm text-slate-500">Ładowanie konfiguracji reklam…</p>
  }
  if (cfg.isError) return <p className="text-sm text-rose-600">Błąd: {String(cfg.error)}</p>
  if (catalogQuery.isError) {
    return <p className="text-sm text-rose-600">Błąd katalogu: {String(catalogQuery.error)}</p>
  }
  if (!cfg.data || !draft || !catalogQuery.data || !catalogDraft) return null

  const bidderCount = Object.keys(draft.bidders ?? {}).length

  const slotProblems = draft.slotBlueprints.filter((bp) =>
    hasBlueprintIssues(blueprintIssues(bp, draft)),
  ).length

  const tabs: { id: Tab; label: string }[] = [
    { id: 'typy', label: `Typy reklam (${catalogDraft.types.filter((t) => !HIDDEN_TYPES.has(t.id)).length})` },
    { id: 'sloty', label: `AdUnity (${catalogDraft.adUnits.filter((u) => !HIDDEN_TYPES.has(u.typeId)).length})` },
    { id: 'dostawcy', label: `Dostawcy (${bidderCount})` },
    { id: 'ustawienia', label: 'Ustawienia' },
    { id: 'raw', label: 'Eksport / Raw JSON' },
  ]

  function confirmSave(): boolean {
    if (
      slotProblems > 0 &&
      !window.confirm(
        `Uwaga: ${slotProblems} slotów może nie dawać bidów ` +
          `(zakładka „AdUnity” → żółte ostrzeżenia). Zapisać mimo to?`,
      )
    ) {
      return false
    }
    return window.confirm(
      'Zapis idzie od razu na produkcję (wspólne Typy/AdUnity dla wszystkich aplikacji). Kontynuować?',
    )
  }

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3">
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{service}</span>
          <span>
            katalog v{catalogQuery.data.version} · {service} v{cfg.data.version} · ostatnio:{' '}
            {cfg.data.updatedBy} ·{' '}
            {cfg.data.version > 0 ? new Date(cfg.data.updatedAt).toLocaleString() : '—'}
          </span>
          {dirty && (
            <Badge variant="outline" className="border-amber-300 text-amber-700">
              niezapisane zmiany
            </Badge>
          )}
          {!draft.prebidEnabled && (
            <button type="button" onClick={() => onTabChange('ustawienia')}>
              <Badge variant="destructive">Prebid OFF — tylko GAM</Badge>
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setCatalogDraft(catalogQuery.data!)
              setDraft(cfg.data!)
              setSaveStatus({ kind: 'idle' })
            }}
            disabled={!dirty}
          >
            Cofnij zmiany
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={() => {
              if (!confirmSave()) return
              save.mutate()
            }}
            disabled={save.isPending || !dirty}
          >
            {save.isPending ? 'Zapisuję…' : 'Zapisz'}
          </Button>
        </div>
      </div>

      <div className="border-b px-5">
        <Tabs value={tab} onValueChange={(v) => onTabChange(v as Tab)}>
          <TabsList className="h-auto w-full justify-start gap-1 overflow-x-auto rounded-none bg-transparent p-0">
            {tabs.map((t) => (
              <TabsTrigger
                key={t.id}
                value={t.id}
                className="rounded-none border-b-2 border-transparent py-3 font-medium data-[state=active]:border-foreground data-[state=active]:shadow-none"
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="p-5">
        {tab === 'typy' && (
          <TypesEditor
            catalog={catalogDraft}
            onChangeCatalog={(next) => {
              setCatalogDraft(next)
              // Zaktualizuj rozmiary/adHigh również w bieżącym podglądzie draftu serwisu
              const tMap = new Map(next.types.map((t) => [t.id, t]))
              const uMap = new Map(next.adUnits.map((u) => [u.slot, u]))
              setDraft({
                ...draft,
                slotBlueprints: draft.slotBlueprints.map((bp) => {
                  const u = uMap.get(bp.slot)
                  const t = u ? tMap.get(u.typeId) : undefined
                  return {
                    ...bp,
                    typeId: u?.typeId ?? bp.typeId,
                    sizes: t ? { ...t.sizes, ...u?.sizes } : bp.sizes,
                    adHigh: u ? u.adHigh : bp.adHigh,
                  }
                }),
              })
            }}
          />
        )}
        {tab === 'sloty' && (
          <SlotsEditor
            catalog={catalogDraft}
            onChangeCatalog={setCatalogDraft}
            draft={draft}
            setDraft={setDraft}
            onGoToTypes={() => onTabChange('typy')}
          />
        )}
        {tab === 'dostawcy' && <BiddersEditor draft={draft} setDraft={setDraft} />}
        {tab === 'ustawienia' && <SettingsEditor draft={draft} setDraft={setDraft} />}
        {tab === 'raw' && <ExportJsonView draft={draft} catalog={catalogDraft} />}

        {saveStatus.kind === 'ok' && (
          <p className="mt-3 text-xs text-emerald-600">{saveStatus.message}</p>
        )}
        {saveStatus.kind === 'err' && (
          <pre className="mt-3 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-destructive/10 p-2 text-xs text-destructive">
            {saveStatus.message}
          </pre>
        )}
      </div>
    </Card>
  )
}

function ExportJsonView({ draft, catalog }: { draft: ServiceConfig; catalog: AdsCatalog }) {
  const [mode, setMode] = useState<'wp' | 'crm' | 'catalog'>('wp')
  const [copied, setCopied] = useState(false)

  const payload = useMemo(() => {
    if (mode === 'wp') return buildWpExportPreview(draft, catalog)
    if (mode === 'catalog') return catalog
    return draft
  }, [mode, draft, catalog])

  const jsonText = useMemo(() => JSON.stringify(payload, null, '\t'), [payload])

  function copyToClipboard() {
    navigator.clipboard.writeText(jsonText).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  function downloadFile() {
    const blob = new Blob([jsonText], { type: 'application/json;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download =
      mode === 'wp'
        ? `ad_slots-${draft.service}.json`
        : mode === 'catalog'
          ? 'ads-catalog.json'
          : `crm-config-${draft.service}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => setMode('wp')}
            className={
              'rounded border px-3 py-1.5 text-xs font-medium ' +
              (mode === 'wp'
                ? 'border-slate-900 bg-slate-900 text-white'
                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50')
            }
          >
            Pełny eksport produkcyjny WP ({draft.service})
          </button>
          <button
            type="button"
            onClick={() => setMode('crm')}
            className={
              'rounded border px-3 py-1.5 text-xs font-medium ' +
              (mode === 'crm'
                ? 'border-slate-900 bg-slate-900 text-white'
                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50')
            }
          >
            Config CRM + Prebid ({draft.service})
          </button>
          <button
            type="button"
            onClick={() => setMode('catalog')}
            className={
              'rounded border px-3 py-1.5 text-xs font-medium ' +
              (mode === 'catalog'
                ? 'border-slate-900 bg-slate-900 text-white'
                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50')
            }
          >
            Wspólny katalog (Typy + AdUnity)
          </button>
        </div>

        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={copyToClipboard}>
            {copied ? 'Skopiowano!' : 'Kopiuj JSON'}
          </Button>
          <Button type="button" size="sm" onClick={downloadFile}>
            Pobierz .json
          </Button>
        </div>
      </div>

      <pre className="json-editor max-h-[65vh] overflow-auto rounded border border-slate-200 bg-slate-50 p-3 text-xs">
        {jsonText}
      </pre>
    </div>
  )
}
